import Papa from 'papaparse'
import { readSheet } from 'read-excel-file/universal'

/** Reading a club's asset file (GK-004): CSV from French Excel (semicolons, Windows-1252, a BOM, quoted line breaks) or .xlsx. Parsing
 *  happens HERE, on the client; the server only ever sees raw cell strings (ARCHITECTURE 7.4, ADR-036). */
export interface ParsedTable {
  headers: string[]
  rows: string[][]
  source: 'csv' | 'xlsx'
  delimiter?: string
  encoding?: 'utf-8' | 'windows-1252'
  warnings: string[]
}
export const MAX_ROWS = 5000

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** UTF-8 if it decodes cleanly, else Windows-1252 (what French Excel writes). */
export function decodeText(bytes: ArrayBuffer): { text: string; encoding: 'utf-8' | 'windows-1252' } {
  try {
    return { text: new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(bytes).replace(/^﻿/, ''), encoding: 'utf-8' }
  } catch {
    return { text: new TextDecoder('windows-1252').decode(bytes), encoding: 'windows-1252' }
  }
}

const clean = (v: unknown): string => String(v ?? '').replace(/ /g, ' ').trim()

/** Date-only spreadsheet cells are UTC midnight: read them with the UTC getters, never the device zone (ARCHITECTURE 15.3). */
export function isoDateUtc(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

function toTable(matrix: string[][], source: 'csv' | 'xlsx', extra: Partial<ParsedTable> = {}): ParsedTable {
  const warnings: string[] = []
  const nonEmpty = matrix.filter((r) => r.some((c) => c !== ''))
  if (!nonEmpty.length) return { headers: [], rows: [], source, warnings: ['empty'], ...extra }
  const width = Math.max(...nonEmpty.map((r) => r.length))
  const seen = new Map<string, number>()
  const headers = Array.from({ length: width }, (_, i) => {
    const h = nonEmpty[0]![i] ?? ''
    const base = h || `Colonne ${i + 1}`
    const n = (seen.get(base) ?? 0) + 1
    seen.set(base, n)
    return n === 1 ? base : `${base} (${n})`
  })
  // drop columns that are empty everywhere (including the header): trailing delimiters
  const keep = headers.map((_, i) => nonEmpty.some((r) => (r[i] ?? '') !== ''))
  const rows = nonEmpty.slice(1).map((r) => headers.map((_, i) => r[i] ?? '').filter((_, i) => keep[i]))
  const kept = headers.filter((_, i) => keep[i])
  if (rows.length > MAX_ROWS) warnings.push('too_many_rows')
  return { headers: kept, rows: rows.slice(0, MAX_ROWS), source, warnings, ...extra }
}

export function parseCsv(text: string): ParsedTable {
  const r = Papa.parse<string[]>(text, { skipEmptyLines: 'greedy', delimitersToGuess: [';', ',', '\t', '|'] })
  return toTable(r.data.map((row) => row.map(clean)), 'csv', { delimiter: r.meta.delimiter })
}

export async function parseXlsx(bytes: ArrayBuffer): Promise<ParsedTable> {
  const data = await readSheet(bytes)
  const cell = (v: unknown): string => {
    if (v instanceof Date) return isoDateUtc(v)
    if (typeof v === 'number') return Number.isFinite(v) ? String(v) : ''
    return clean(v)
  }
  return toTable(data.map((row) => row.map(cell)), 'xlsx')
}

export async function readTableFile(file: { name: string; arrayBuffer(): Promise<ArrayBuffer> }): Promise<{ table: ParsedTable; sha256: string }> {
  const bytes = await file.arrayBuffer()
  const sha256 = await sha256Hex(bytes)
  if (/\.xlsx$/i.test(file.name)) return { table: await parseXlsx(bytes), sha256 }
  const { text, encoding } = decodeText(bytes)
  return { table: { ...parseCsv(text), encoding }, sha256 }
}
